import { useState } from "react";
import { FileBadge, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { download, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shared";
import ExecutiveReport from "@/components/ExecutiveReport";

const REPORTS = [
  ["daily_fleet", "Daily fleet", "Journeys, distance and reconciliation counts per vehicle"],
  ["journeys", "Journeys", "All physical journeys with driver, distance, stops and GPS gaps"],
  ["trips", "Uber trips", "Declared trips as imported"],
  ["reconciliation", "Reconciliation", "Every record with score, status, reasons and decisions"],
  ["unmatched", "Unmatched / review", "Only records that require attention"],
  ["events", "Device events", "Door, ignition and vehicle start/stop events"],
  ["vehicles", "Vehicles", "Fleet register"],
  ["drivers", "Drivers", "Driver register"],
  ["passengers", "Passengers", "Inferred passenger journeys with confidence and evidence"],
  ["discrepancies", "Discrepancies", "Only records with at least one mismatching factor"],
];

export default function Reports() {
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState("");
  const run = async (kind: string, format: string) => {
    setBusy(kind + format);
    try { await download(`/reports/${kind}?format=${format}&hours=${hours}`, `fleetlio-${kind}.${format}`); toast.success("Report downloaded"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="An AI-written executive report, plus raw CSV / XLSX / PDF exports of every underlying table" actions={
        <select data-testid="reports-range-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100" value={hours} onChange={(e) => setHours(+e.target.value)}>
          {[[24, "Last 24 h"], [48, "Last 48 h"], [168, "Last 7 days"], [720, "Last 30 days"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>} />
      <ExecutiveReport />
      <div className="font-mono text-[11px] text-slate-500 mb-2 uppercase tracking-wide">Raw data exports</div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {REPORTS.map(([k, title, desc]) => (
          <div key={k} data-testid={`report-card-${k}`} className="tg-card tg-card-hover p-5 flex flex-col gap-3">
            <div><div className="font-display text-lg text-slate-100">{title}</div><div className="text-xs text-slate-400 mt-1">{desc}</div></div>
            <div className="flex gap-2 mt-auto">
              <Button size="sm" variant="outline" data-testid={`report-${k}-csv-button`} disabled={!!busy} onClick={() => run(k, "csv")} className="border-[#26334d] bg-transparent text-slate-200 hover:bg-[#182030]"><FileText className="h-4 w-4 mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" data-testid={`report-${k}-xlsx-button`} disabled={!!busy} onClick={() => run(k, "xlsx")} className="border-[#26334d] bg-transparent text-slate-200 hover:bg-[#182030]"><FileSpreadsheet className="h-4 w-4 mr-1" /> XLSX</Button>
              <Button size="sm" data-testid={`report-${k}-pdf-button`} disabled={!!busy} onClick={() => run(k, "pdf")} className="bg-blue-600 hover:bg-blue-500 text-white"><FileBadge className="h-4 w-4 mr-1" /> PDF</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
